import { useState } from "react";
import { t } from "i18next";
import type { NavigateFunction } from "react-router-dom";
import { CirclePlay, Eye } from "lucide-react";

import { RowActionsMenu } from "@/core/components/shared/data-table";
import { canSimulatePayrollBatch } from "@/features/payroll/components/can-simulate-payroll-batch";
import { PayrollSimulatePreview } from "@/features/payroll/components/payroll-simulate-preview";
import type { PayrollBatchRow } from "@/features/payroll/components/batches/payroll-batches-columns";

/**
 * Menu ⋮ de chaque ligne sur `/app/payroll`. « Voir » ouvre le détail.
 * « Simuler » n'appelle pas l'API : il ouvre la même `ShareablePreview`
 * que la fiche. Masqué quand le statut de la ligne (ou un lot vide)
 * rend la simulation impossible — la liste ne porte pas les lignes.
 */
export function PayrollBatchRowActions({
  batch,
  navigate,
}: {
  batch: PayrollBatchRow;
  navigate: NavigateFunction;
}) {
  const [simulateOpen, setSimulateOpen] = useState(false);
  const canSimulate = canSimulatePayrollBatch(batch);

  return (
    <>
      <RowActionsMenu
        actions={[
          {
            id: "view",
            label: t("payroll.batches.actions.view"),
            icon: Eye,
            onClick: () => navigate(`/app/payroll/${batch.id}`),
          },
          ...(canSimulate
            ? [
                {
                  id: "simulate",
                  label: t("payroll.actions.simulate.cta"),
                  icon: CirclePlay,
                  onClick: () => setSimulateOpen(true),
                },
              ]
            : []),
        ]}
      />

      <PayrollSimulatePreview
        batchId={batch.id}
        label={batch.label}
        lineCount={batch.lineCount}
        open={simulateOpen}
        onOpenChange={setSimulateOpen}
      />
    </>
  );
}
